import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, bearerHeaders, resolveUrl, type MarkCamera } from '../api.ts';
import { Camera, Copy, MessageSquare, Send, Trash2 } from 'lucide-react';
import { RADIUS, TYPE, getTheme, useThemeMode } from '../theme';
import { Badge, Button, Card, Field, Icon, IconButton, Input, Loader, SectionHeader, type BadgeTone } from '../components/ui';
import { apiFetch } from '../appFetch';
import { useIntegrationVisibility, type IntegrationKey } from '../lib/integrationVisibility';

export function IntegrationPanel({ markId, markLat, markLng }: { markId: string; markLat?: number | null; markLng?: number | null }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);

  const token = localStorage.getItem('qvenari_access_token') || '';
  const H = { 'Content-Type': 'application/json', ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' };

  // IN3: integrationer som plattformsadmin dolt visas inte; admin ser dem märkta.
  const { shown: show, hiddenForUsers } = useIntegrationVisibility();
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tgToken, setTgToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [tgBusy, setTgBusy] = useState(false);
  const [cameras, setCameras] = useState<MarkCamera[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(true);
  const [newCameraName, setNewCameraName] = useState('');
  const [cameraBusy, setCameraBusy] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [waQR, setWaQR] = useState<string | null>(null);
  const [waQrHint, setWaQrHint] = useState<string | null>(null);
  const [waPolling, setWaPolling] = useState(false);
  const waPollRef = useRef<number | null>(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsSyncBusy, setSmsSyncBusy] = useState(false);

  const loadCameras = useCallback(async () => {
    try { setCameras(await api.markCameras(markId)); } catch (e) { console.error(e); }
    setCamerasLoading(false);
  }, [markId]);

  const addCamera = async () => {
    const name = newCameraName.trim();
    if (!name) return;
    let lat = markLat ?? null, lng = markLng ?? null;
    if (lat == null || lng == null) {
      const pos = await new Promise<GeolocationPosition | null>(res => navigator.geolocation ? navigator.geolocation.getCurrentPosition(res, () => res(null), { timeout: 8000 }) : res(null));
      if (!pos) { alert('Marken saknar kartposition. Lägg till kameran från kartan i stället (Karta → Åtelkamera).'); return; }
      lat = pos.coords.latitude; lng = pos.coords.longitude;
    }
    setCameraBusy(true);
    try {
      const cam = await api.createMarkCamera({ markId, name, lat, lng });
      setCameras(prev => [...prev, cam]);
      setNewCameraName('');
    } catch (e: any) { alert(e?.message || 'Kunde inte lägga till kameran'); }
    setCameraBusy(false);
  };

  const removeCamera = async (cam: MarkCamera) => {
    if (!confirm(`Ta bort ${cam.name}? Adressen ${cam.emailAddress} slutar fungera. Bilderna finns kvar i albumet.`)) return;
    try { await api.deleteMarkCamera(cam.id); setCameras(prev => prev.filter(c => c.id !== cam.id)); }
    catch (e: any) { alert(e?.message || 'Kunde inte ta bort kameran'); }
  };

  const copyText = (text: string) => {
    if (navigator.clipboard) { navigator.clipboard.writeText(text); } else { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    alert('Kopierat!');
  };

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/integrations/${markId}`, { headers: H });
      if (r.ok) setIntegrations(await r.json());
    } catch {}
    setLoading(false);
  }, [markId]);

  useEffect(() => {
    load();
    loadCameras();
    return () => { if (waPollRef.current) clearInterval(waPollRef.current); };
  }, [load, loadCameras]);

  useEffect(() => {
    const wa = show('whatsapp') ? integrations.find((i: any) => i.platform === 'whatsapp') : undefined;
    if (wa && !wa.connected) {
      if (!waQR) {
        apiFetch(`/api/integrations/${markId}/whatsapp/qr`, { headers: H })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.qr) { setWaQR(d.qr); setWaQrHint(null); } })
          .catch(() => {});
      }
      if (!waPollRef.current) {
        waPollRef.current = window.setInterval(async () => {
          try {
            const ir = await apiFetch(`/api/integrations/${markId}`, { headers: H });
            if (ir.ok) {
              const ints = await ir.json() as any[];
              setIntegrations(ints);
              const w = ints.find((i: any) => i.platform === 'whatsapp');
              if (w?.connected) { clearInterval(waPollRef.current!); waPollRef.current = null; setWaQR(null); setWaQrHint(null); setWaPolling(false); return; }
            }
            if (!integrations.find((i: any) => i.platform === 'whatsapp' && i.connected)) {
              const qr = await apiFetch(`/api/integrations/${markId}/whatsapp/qr`, { headers: H });
              if (qr.ok) { const d = await qr.json() as any; if (d.qr) { setWaQR(d.qr); setWaQrHint(null); } }
            }
          } catch {}
        }, 3000);
      }
    }
    if (wa?.connected && waPollRef.current && !waPolling) { clearInterval(waPollRef.current); waPollRef.current = null; }
  }, [integrations]);

  const startQRPolling = useCallback(() => {
    setWaPolling(true);
    let attempts = 0;
    if (waPollRef.current) clearInterval(waPollRef.current);
    waPollRef.current = window.setInterval(async () => {
      attempts++;
      try {
        const ir = await apiFetch(`/api/integrations/${markId}`, { headers: H });
        if (ir.ok) {
          const ints = await ir.json() as any[];
          const wa = ints.find((i: any) => i.platform === 'whatsapp');
          if (wa?.connected) { clearInterval(waPollRef.current!); waPollRef.current = null; setWaPolling(false); setWaQR(null); setWaQrHint(null); await load(); return; }
        }
        const qr = await apiFetch(`/api/integrations/${markId}/whatsapp/qr`, { headers: H });
        if (qr.ok) { const d = await qr.json() as any; if (d.qr) { setWaQR(d.qr); setWaQrHint(null); } }
      } catch {}
      if (attempts > 40) { clearInterval(waPollRef.current!); waPollRef.current = null; setWaPolling(false); await load(); }
    }, 3000);
  }, [markId]);

  async function saveSms() {
    setSmsBusy(true);
    try {
      const r = await apiFetch(`/api/integrations/${markId}/sms`, { method: 'POST', headers: H, body: JSON.stringify({}) });
      const d = await r.json() as any;
      if (!r.ok) { alert('Fel: ' + (d.error ?? `HTTP ${r.status}`)); setSmsBusy(false); return; }
      await load(); alert(d.message ?? 'SMS-integration sparad!');
    } catch (e: any) { alert('Fel: ' + e.message); }
    setSmsBusy(false);
  }

  async function removeSms() {
    if (!confirm('Ta bort SMS-integration?')) return;
    await apiFetch(`/api/integrations/${markId}/sms`, { method: 'DELETE', headers: H });
    await load();
  }

  async function syncSmsMembers() {
    setSmsSyncBusy(true);
    try {
      const r = await apiFetch(`/api/integrations/${markId}/sms/sync-members`, { method: 'POST', headers: H });
      const d = await r.json() as any;
      alert(d.success ? `Synkroniserat ${d.membersSync} medlemmar` : `Synk misslyckades: ${d.status}`);
    } catch (e: any) { alert('Fel: ' + e.message); }
    setSmsSyncBusy(false);
  }

  const tgRow = integrations.find(i => i.platform === 'telegram');
  const waRow = integrations.find(i => i.platform === 'whatsapp');
  const smsRow = integrations.find(i => i.platform === 'sms');

  async function saveTelegram() {
    setTgBusy(true);
    try {
      await apiFetch(`/api/integrations/${markId}/telegram`, { method: 'POST', headers: H, body: JSON.stringify({ botToken: tgToken, chatId: tgChatId || undefined, active: true }) });
      await load(); setTgToken(''); setTgChatId(''); alert('Telegram bot sparad och startad!');
    } catch (e: any) { alert('Fel: ' + e.message); }
    setTgBusy(false);
  }

  async function removeTelegram() {
    if (!confirm('Ta bort Telegram-integration?')) return;
    await apiFetch(`/api/integrations/${markId}/telegram`, { method: 'DELETE', headers: H });
    await load();
  }

  async function startWhatsAppQR() {
    setWaBusy(true); setWaQR(null); setWaQrHint(null);
    try {
      const r = await apiFetch(`/api/integrations/${markId}/whatsapp/start`, { method: 'POST', headers: H, body: JSON.stringify({}) });
      if (!r.ok) { const d = (await r.json().catch(() => ({}))) as any; const msg = d.error ?? `HTTP ${r.status}`; setWaQrHint(msg); alert('Fel: ' + msg); setWaBusy(false); return; }
      let gotQR = false; let lastQrErr = '';
      for (let i = 0; i < 24; i++) {
        await new Promise(res => setTimeout(res, 2000));
        try {
          const qr = await apiFetch(`/api/integrations/${markId}/whatsapp/qr`, { headers: H });
          if (qr.ok) { const d = await qr.json() as any; if (d.qr) { setWaQR(d.qr); setWaQrHint(null); gotQR = true; break; } }
          else { const errBody = (await qr.json().catch(() => ({}))) as any; lastQrErr = errBody.error || `HTTP ${qr.status}`; }
        } catch (e: any) { lastQrErr = e?.message || 'Nätverksfel'; }
      }
      if (!gotQR) { setWaQrHint(lastQrErr || 'Ingen QR ännu efter ~48 s — bakgrundspollning fortsätter.'); }
      startQRPolling();
    } catch (e: any) { setWaQrHint(e.message); alert('Fel: ' + e.message); }
    setWaBusy(false);
  }

  async function removeWhatsApp() {
    if (!confirm('Ta bort WhatsApp-integration?')) return;
    setWaQR(null); setWaQrHint(null);
    if (waPollRef.current) clearInterval(waPollRef.current);
    await apiFetch(`/api/integrations/${markId}/whatsapp`, { method: 'DELETE', headers: H });
    await load();
  }

  const status = (tone: BadgeTone, text: string, key?: IntegrationKey) => <>{key && hiddenForUsers(key) && <Badge tone="info">Dold för användare</Badge>}<Badge tone={tone} dot>{text}</Badge></>;
  const header = (icon: React.ReactNode, name: string, badge: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <span aria-hidden style={{ width: 44, height: 44, borderRadius: RADIUS.md, background: T.surface2, color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ ...TYPE.title, fontSize: 16, color: T.text }}>{name}</div>
        {badge}
      </div>
    </div>
  );
  const help: React.CSSProperties = { color: T.textSub, fontSize: 14, lineHeight: 1.55, margin: 0 };
  const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap' };
  const cardStyle: React.CSSProperties = { marginBottom: 16 };
  const chatNames = ([['telegram', 'Telegram'], ['whatsapp', 'WhatsApp'], ['sms', 'SMS']] as const).filter(([k]) => show(k)).map(([, n]) => n);

  if (loading) return <Loader text="Laddar integrationer…" />;

  return (
    <div style={{ maxWidth: 680 }}>
      <SectionHeader title="Integrationer" />
      <p style={{ ...help, margin: '-6px 0 20px' }}>{chatNames.length ? `Koppla ${chatNames.length > 1 ? `${chatNames.slice(0, -1).join(', ')} och ${chatNames[chatNames.length - 1]}` : chatNames[0]} till gruppchatten, och åtelkameror till albumet och kartan.` : 'Koppla åtelkameror till albumet och kartan.'}</p>

      {show('telegram') && <Card style={cardStyle}>
        {header(<Icon as={Send} />, 'Telegram', status(tgRow?.connected ? 'ok' : 'neutral', tgRow?.connected ? 'Ansluten' : tgRow ? 'Ej ansluten' : 'Inte konfigurerad', 'telegram'))}
        {tgRow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <p style={help}>Chat-ID: {tgRow.chat_id || 'väntar på första meddelandet…'}</p>
            <Button variant="danger" onClick={removeTelegram}>Ta bort</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Bot-token (från @BotFather)"><Input value={tgToken} onChange={e => setTgToken(e.target.value)} placeholder="1234567890:AAF..." /></Field>
            <Field label="Gruppens chat-ID" hint="Valfritt – hämtas automatiskt."><Input value={tgChatId} onChange={e => setTgChatId(e.target.value)} placeholder="-1001234567890" /></Field>
            <ol style={{ ...help, paddingLeft: 20 }}>
              <li>Skapa en bot via @BotFather i Telegram.</li>
              <li>Kopiera bot-token och klistra in ovan.</li>
              <li>Lägg till boten i din Telegram-grupp.</li>
              <li>Skicka ett meddelande i gruppen – chat-ID sparas automatiskt.</li>
            </ol>
            <Button onClick={saveTelegram} disabled={!tgToken} loading={tgBusy} style={{ alignSelf: 'flex-start' }}>{tgBusy ? 'Sparar…' : 'Spara och starta bot'}</Button>
          </div>
        )}
      </Card>}

      {show('whatsapp') && <Card style={cardStyle}>
        {header(
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#25D366" aria-hidden><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.523 5.849L.057 23.786a.5.5 0 0 0 .613.635l6.077-1.578A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.693-.502-5.24-1.382l-.374-.217-3.876 1.007 1.013-3.762-.232-.382A9.94 9.94 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>,
          'WhatsApp',
          status(waRow?.connected ? 'ok' : waQR ? 'accent' : 'neutral', waRow?.connected ? 'Ansluten' : waQR ? 'Väntar på QR-skanning' : waRow ? 'Ej ansluten' : 'Inte konfigurerad', 'whatsapp'),
        )}
        {waRow?.connected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <p style={help}>Grupp: {waRow.chat_id || 'alla grupper synkas'}</p>
            <Button variant="danger" onClick={removeWhatsApp}>Koppla bort</Button>
          </div>
        ) : waQR ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>Skanna QR-koden med WhatsApp</div>
            <div style={{ background: '#fff', borderRadius: RADIUS.md, padding: 16, maxWidth: '100%' }}><img src={waQR} alt="WhatsApp QR-kod" style={{ width: 'min(320px, 70vw)', height: 'auto', aspectRatio: '1', display: 'block', imageRendering: 'pixelated' }} /></div>
            <ol style={{ ...help, paddingLeft: 20 }}>
              <li>Öppna WhatsApp på telefonen.</li>
              <li>Gå till <strong style={{ color: T.text }}>Inställningar → Länkade enheter</strong>.</li>
              <li>Tryck <strong style={{ color: T.text }}>Länka en enhet</strong>.</li>
              <li>Skanna QR-koden ovan med kameran.</li>
            </ol>
            {waPolling && <div role="status" style={{ ...help, color: T.accentText, fontWeight: 600 }}>Väntar på att du skannar QR-koden…</div>}
            <div style={row}>
              <Button variant="secondary" onClick={() => { setWaQR(null); setWaQrHint(null); if (waPollRef.current) clearInterval(waPollRef.current); load(); }}>Avbryt</Button>
              <Button onClick={load}>Uppdatera status</Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <p style={help}>Koppla ditt WhatsApp-konto genom att skanna en QR-kod, som när du länkar WhatsApp Web.</p>
            <div style={row}>
              <Button onClick={startWhatsAppQR} loading={waBusy}>{waBusy ? 'Startar…' : 'Visa QR-kod'}</Button>
              {waRow && <Button variant="danger" onClick={removeWhatsApp}>Ta bort integration</Button>}
            </div>
            {waQrHint && !waQR && <div role="alert" style={{ ...help, color: T.danger }}>{waQrHint}</div>}
          </div>
        )}
      </Card>}

      <Card style={cardStyle}>
        {header(<Icon as={Camera} />, 'Åtelkameror', status(cameras.length ? 'ok' : 'neutral', cameras.length ? `${cameras.length} ${cameras.length === 1 ? 'kamera' : 'kameror'}` : 'Inga kameror'))}
        <p style={{ ...help, marginBottom: 12 }}>Varje kamera får en egen e-postadress. Ange den som mottagare i kamerans e-post- eller SMTP-inställningar (eller i tillverkarens app). Bilderna visas i gruppchatten, albumet och på kamerans nål på kartan.</p>
        {camerasLoading ? <Loader inline /> : cameras.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {cameras.map((cam, i) => (
              <div key={cam.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                {cam.lastImageUrl
                  ? <img src={resolveUrl(cam.lastImageUrl)} alt="" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: RADIUS.sm, flexShrink: 0 }} />
                  : <div aria-hidden style={{ width: 64, height: 48, borderRadius: RADIUS.sm, flexShrink: 0, background: T.surface2, color: T.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon as={Camera} /></div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>{cam.name}</div>
                  <div style={{ color: T.accentText, fontFamily: 'ui-monospace, monospace', fontSize: 13, wordBreak: 'break-all', margin: '2px 0' }}>{cam.emailAddress}</div>
                  <div style={{ ...TYPE.small, color: T.textMuted }}>
                    {cam.lastImageAt ? `Senaste bild ${new Date(cam.lastImageAt).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Ingen bild än'}
                    {cam.lat == null ? ' · saknar position' : ''}
                  </div>
                </div>
                <IconButton label={`Kopiera adressen till ${cam.name}`} onClick={() => copyText(cam.emailAddress)}><Icon as={Copy} size={18} /></IconButton>
                <IconButton label={`Ta bort ${cam.name}`} onClick={() => removeCamera(cam)} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Field label="Ny kamera" style={{ flex: 1, minWidth: 0 }}><Input value={newCameraName} onChange={e => setNewCameraName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCamera(); }} placeholder="Kamerans namn, t.ex. Gläntan" maxLength={80} /></Field>
          <Button onClick={addCamera} disabled={!newCameraName.trim()} loading={cameraBusy}>{cameraBusy ? 'Skapar…' : 'Lägg till'}</Button>
        </div>
        <p style={{ ...TYPE.small, color: T.textMuted, marginTop: 8, marginBottom: 0 }}>Nya kameror hamnar i markens mitt – dra kamerans nål på kartan till rätt plats.</p>
      </Card>

      {show('sms') && <Card style={cardStyle}>
        {header(<Icon as={MessageSquare} />, 'SMS (Supertext)', status(smsRow?.connected ? 'ok' : 'neutral', smsRow?.connected ? 'Ansluten' : smsRow ? 'Ej ansluten' : 'Inte konfigurerad', 'sms'))}
        {smsRow?.connected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <p style={help}>Grupp-ID: <span style={{ fontFamily: 'ui-monospace, monospace', color: T.accentText }}>{smsRow.chat_id}</span></p>
            <div style={row}>
              <Button onClick={syncSmsMembers} loading={smsSyncBusy}>{smsSyncBusy ? 'Synkar…' : 'Synka medlemmar'}</Button>
              <Button variant="danger" onClick={removeSms}>Koppla bort</Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <p style={help}>Koppla marken till en Supertext SMS-grupp. Alla markmedlemmar med mobilnummer läggs till automatiskt.</p>
            <div style={row}>
              <Button onClick={saveSms} loading={smsBusy}>{smsBusy ? 'Skapar grupp…' : 'Koppla SMS-grupp'}</Button>
              {smsRow && <Button variant="danger" onClick={removeSms}>Ta bort integration</Button>}
            </div>
          </div>
        )}
      </Card>}

      {show('telegram') && <p style={{ ...TYPE.small, color: T.textMuted, lineHeight: 1.6 }}>Meddelanden från {show('whatsapp') ? 'Telegram och WhatsApp' : 'Telegram'} visas i gruppchatten och tvärtom.{show('whatsapp') ? ' WhatsApp-kopplingen använder WhatsApp Web-protokollet.' : ''}</p>}
    </div>
  );
}
